import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Loader2, Wand2, Award, Clock, BookOpen } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface BadgeCreatorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBadgeCreated: () => void;
}

export function BadgeCreator({ open, onOpenChange, onBadgeCreated }: BadgeCreatorProps) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imagePrompt: '',
    requiredCourseCount: '',
    requiredPdHours: '',
  });
  const [creating, setCreating] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [criteriaType, setCriteriaType] = useState<'courses' | 'hours'>('courses');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        imagePrompt: formData.imagePrompt.trim() || formData.name.trim(),
        requiredCourseCount: criteriaType === 'courses' && formData.requiredCourseCount 
          ? parseInt(formData.requiredCourseCount) 
          : null,
        requiredPdHours: criteriaType === 'hours' && formData.requiredPdHours 
          ? parseInt(formData.requiredPdHours) 
          : null,
      };

      console.log('Creating badge with payload:', payload);

      const { data, error } = await supabase.functions.invoke('generate-badge', {
        body: payload
      });

      if (error) {
        console.error('Error creating badge:', error);
        throw error;
      }

      console.log('Badge creation response:', data);

      if (data.success) {
        toast.success(`Badge "${formData.name}" created successfully!`);
        setFormData({
          name: '',
          description: '',
          imagePrompt: '',
          requiredCourseCount: '',
          requiredPdHours: '',
        });
        onBadgeCreated();
        onOpenChange(false);
      } else {
        throw new Error(data.error || 'Failed to create badge');
      }
    } catch (error) {
      console.error('Badge creation error:', error);
      toast.error('Failed to create badge: ' + (error as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const generateImagePrompt = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a badge name first');
      return;
    }

    setGeneratingImage(true);
    try {
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${import.meta.env.VITE_GOOGLE_AI_API_KEY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: `Create a detailed image prompt for a professional achievement badge called "${formData.name}". 
              Description: ${formData.description}
              
              The prompt should describe a clean, modern badge design suitable for professional development recognition. 
              Include colors, symbols, and design elements that reflect the achievement. 
              Keep it concise but descriptive for image generation.`
            }]
          }],
          generationConfig: {
            temperature: 0.7,
            maxOutputTokens: 200,
          }
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const generatedPrompt = data.candidates[0].content.parts[0].text;
        setFormData(prev => ({ ...prev, imagePrompt: generatedPrompt }));
        toast.success('Image prompt generated!');
      } else {
        throw new Error('Failed to generate image prompt');
      }
    } catch (error) {
      console.error('Error generating image prompt:', error);
      toast.error('Failed to generate image prompt');
    } finally {
      setGeneratingImage(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Create New Badge
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Badge Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Course Completion Champion"
                required
              />
            </div>

            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what this badge represents and how to earn it"
                rows={3}
                required
              />
            </div>
          </div>

          {/* Criteria Selection */}
          <div className="space-y-4">
            <Label>Badge Criteria</Label>
            <Tabs value={criteriaType} onValueChange={(value) => setCriteriaType(value as 'courses' | 'hours')}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="courses" className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Course Count
                </TabsTrigger>
                <TabsTrigger value="hours" className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  PD Hours
                </TabsTrigger>
              </TabsList>

              <TabsContent value="courses" className="space-y-2">
                <Label htmlFor="courseCount">Required Number of Courses</Label>
                <Input
                  id="courseCount"
                  type="number"
                  min="1"
                  value={formData.requiredCourseCount}
                  onChange={(e) => setFormData(prev => ({ ...prev, requiredCourseCount: e.target.value }))}
                  placeholder="e.g., 5"
                />
                <p className="text-sm text-muted-foreground">
                  Users must complete this many courses to earn the badge
                </p>
              </TabsContent>

              <TabsContent value="hours" className="space-y-2">
                <Label htmlFor="pdHours">Required Professional Development Hours</Label>
                <Input
                  id="pdHours"
                  type="number"
                  min="1"
                  value={formData.requiredPdHours}
                  onChange={(e) => setFormData(prev => ({ ...prev, requiredPdHours: e.target.value }))}
                  placeholder="e.g., 20"
                />
                <p className="text-sm text-muted-foreground">
                  Users must accumulate this many PD hours to earn the badge
                </p>
              </TabsContent>
            </Tabs>
          </div>

          {/* AI Image Generation */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="imagePrompt">Badge Image Prompt</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={generateImagePrompt}
                    disabled={generatingImage || !formData.name.trim()}
                    className="flex items-center gap-2"
                  >
                    {generatingImage ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Wand2 className="h-4 w-4" />
                    )}
                    Generate with AI
                  </Button>
                </div>
                <Textarea
                  id="imagePrompt"
                  value={formData.imagePrompt}
                  onChange={(e) => setFormData(prev => ({ ...prev, imagePrompt: e.target.value }))}
                  placeholder="Describe the visual design for your badge..."
                  rows={3}
                />
                <p className="text-sm text-muted-foreground">
                  Describe how you want the badge to look. Leave empty to use the badge name as the prompt.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Preview */}
          {formData.name && (
            <Card>
              <CardContent className="pt-6">
                <Label className="mb-3 block">Preview</Label>
                <div className="flex items-center gap-4 p-4 border rounded-lg">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary/80 rounded-full flex items-center justify-center">
                    <Award className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold">{formData.name}</h4>
                    <p className="text-sm text-muted-foreground">{formData.description}</p>
                    <div className="flex gap-2 mt-2">
                      {criteriaType === 'courses' && formData.requiredCourseCount && (
                        <Badge variant="secondary">
                          {formData.requiredCourseCount} courses required
                        </Badge>
                      )}
                      {criteriaType === 'hours' && formData.requiredPdHours && (
                        <Badge variant="secondary">
                          {formData.requiredPdHours} PD hours required
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Creating Badge...
                </>
              ) : (
                'Create Badge'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}